import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { languageApi } from '@/domains/system-management/apis/language-management';
import type {
  Language,
  Params,
  UpdateLanguageResponse,
} from '@/domains/system-management/types/language';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useLanguageManagement = (params: Params) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get language list
  const useLanguages = () => {
    return useQuery({
      queryKey: ['languages', params],
      queryFn: () => languageApi.getLanguages(params),
    });
  };

  // Get language details
  const useLanguage = (tag: string) => {
    return useQuery({
      queryKey: ['language', tag],
      queryFn: () => languageApi.getLanguage(tag),
      enabled: !!tag,
    });
  };

  // Create language
  const useCreateLanguage = (onCreateSuccess?: (response: { tag: string }) => void) => {
    return useMutation({
      mutationFn: (data: Partial<Language>) => languageApi.createLanguage(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['languages', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update language
  const useUpdateLanguage = (onUpdateSuccess?: (response: UpdateLanguageResponse) => void) => {
    return useMutation({
      mutationFn: ({ tag, data }: { tag: string; data: Partial<Language> }) =>
        languageApi.updateLanguage(tag, data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['languages', params] });
            queryClient.invalidateQueries({
              queryKey: ['language', responseData.tag],
            });
            if (onUpdateSuccess) onUpdateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Delete language
  const useDeleteLanguage = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (tag: string) => languageApi.deleteLanguage(tag),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['languages', params] });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useLanguages,
    useLanguage,
    useCreateLanguage,
    useUpdateLanguage,
    useDeleteLanguage,
  };
};
